<template>
  <div
    class="language-selector"
  >
    <ul class="flex">
      <li
        v-for="(lang, index) in languages"
        :key="lang.key"
        class="text-sm text-black"
      >
        <KfButton
          class="text-sm text-black uppercase hover:underline hover:underline-offset-4 underline-offset-4"
          :class="{'underline': currentLocale === lang.locale}"
          @click="languageRedirect(lang)"
        >
          <abbr :title="lang.label">{{ lang.locale }}</abbr>
        </KfButton>
        <span
          v-if="index < languages.length - 1"
          class="mx-2"
        >|</span>
      </li>
    </ul>
    <modal
      v-show="modalOpen"
      :modal="true"
      class="language-modal"
      hide-close-button
      @close="modalOpen = false"
    >
      <template slot="body">
        <div class="hc-stage-content">
          <h3 class="h3">
            {{ otherLanguage.notFoundModalTitle }}
          </h3>
          <p class="text-base my-4">
            {{ otherLanguage.notFoundModalDescription }}
          </p>
          <!-- eslint-disable vue/no-v-html -->
          <div class="flex flex-col lg:flex-row gap-6 my-4">
            <kf-link
              :href="otherLanguage.homepageButtonUrl"
              class="btn"
            >
              <KfButton
                class="btn "
                type="primary"
                aria-disabled="true"
              >
                {{ otherLanguage.homepageButtonTitle }}
              </KfButton>
            </kf-link>
            <KfButton
              class="btn"
              type="secondary"
              @click="closeModal()"
            >
              {{ otherLanguage.backButtonTitle }}
            </KfButton>
          </div>
          <kf-rich-text class="pt-4 text-xs">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="otherLanguage.notFoundModalFooter" />
          </kf-rich-text>

          <slot name="locator" />
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import KfButton from '@/components/base/button/Button.vue';
import KfLink from '@/components/base/link/Link.vue';
import KfRichText from '@/components/base/rich-text/RichText.vue';
import Modal from '@/components/modal/Modal.vue';

export default {
  name: 'KfLanguageSelector',
  components: {
    KfLink,
    Modal,
    KfButton,
    KfRichText
  },
  props: {
    languages: {
      type: Array,
      default: () => [],
    },
    currentLocale: {
      type: String,
      default: 'nl',
    },
  },
  data() {
    return {
      isOpen: false,
      modalOpen: false,
      current: this.currentLocale,
      selectedLocale: this.currentLocale,
    };
  },
  computed: {
    currentLanguage() {
      return this.languages.find((lang) => lang.locale === this.current) || {};
    },
    otherLanguage() {
      return this.languages.find(lang => lang.locale !== this.current) || {};
    },
    languagesList() {
      return this.languages.filter(lang => lang.locale !== this.current);
    },
  },
  methods: {
    languageRedirect(lang) {
      if (!lang || lang.locale === this.currentLocale) return;

      this.selectedLocale = lang.locale;

      if (lang.path) {
        window.location.href = lang.path;
      } else {
        this.modalOpen = true;
        document.body.style.overflow = 'hidden';
      }
    },
    closeModal() {
      this.modalOpen = false;
      document.body.style.overflow = 'auto';
    }
  }
};
</script>

<style lang="postcss">
.language-selector {
  @apply relative flex justify-end;

  abbr {
    @apply no-underline cursor-help;
  }

  .btn-toggle {
    @apply flex flex-row bg-white items-center justify-between z-20 w-full text-sm text-black rounded-lg pr-7 pl-2 border-0;
    min-width: auto;
  }

  .btn-label {
    @apply block w-full text-left font-normal;

    &::before {
      @apply block absolute z-20 pt-6;
      content: '';
      width: 10px;
      height: auto;
      right: 8px;
      top: 0;
      transition: transform 100ms ease-in-out;
      background-image: url('~@/assets/svg/chevron-down-secondary.svg?inline');
      background-repeat: no-repeat;
      transform-origin: center;
      stroke: var(--color-primary-bravo);
      background-position: center;
      background-size: contain;

      @screen md {
        @apply mb-2;
        right: 8px;
      }
    }

    .theme-secondary & {
      &::before {
        background-image: url('~@/assets/svg/theme-secondary/ts-chevron-down-secondary.svg?inline');
      }
    }
  }

  .languages {
    @apply hidden absolute top-6 pt-4 pb-2 px-2 w-full bg-white shadow rounded-b-xs;
    z-index: 1;

    &-list {
      @apply list-none;

      &-item {
        @apply mb-3;

        &:last-of-type {
          @apply mb-0;
        }
      }

      &-link {
        @apply block text-left text-black text-sm no-underline;
      }
    }
  }

  &.is-open {
    .languages {
      @apply block border-t-0;
    }

    .btn-label {
      &::before {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
